import { useState, useMemo, useEffect } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { isOwnerOrAdmin } from '../lib/roles';
import { FEATURES, NAV_GROUPS, getFeaturesByGroup, GROUP_DEFAULTS, roleToGroup, type FeatureDef } from '../lib/featureRegistry';
import { useFeatureToggles } from '../components/FeatureToggleProvider';
import { saveFeatureToggle, getFeatureToggles, type GetFeatureTogglesOutputType } from 'zite-endpoints-sdk';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Search, Shield, ChevronDown, ChevronRight, Download, Eye, BookOpen, User, Building2, Users, Settings, Home, RefreshCw, ArrowUpFromLine } from 'lucide-react';
import StaffReadinessIntegration from '../components/admin/StaffReadinessIntegration';
import { formatDistanceToNow, parseISO } from 'date-fns';

const GROUP_ICON: Record<string, typeof Home> = { Home, Learning: BookOpen, 'My Stuff': User, 'Pub & Tools': Building2, Manager: Users, Admin: Settings };

function ToggleGroup({ group, toggleMap, onToggle, onBulkToggle, search }: {
  group: string;
  toggleMap: Map<string, boolean>;
  onToggle: (key: string, enabled: boolean) => void;
  onBulkToggle: (keys: string[], enabled: boolean) => void;
  search: string;
}) {
  const [expanded, setExpanded] = useState(true);
  const features = getFeaturesByGroup(group);
  const filtered = search ? features.filter(f => f.label.toLowerCase().includes(search.toLowerCase()) || f.key.toLowerCase().includes(search.toLowerCase())) : features;
  if (filtered.length === 0) return null;

  const enabledCount = filtered.filter(f => toggleMap.get(f.key) !== false).length;
  const allOn = enabledCount === filtered.length;
  const allOff = enabledCount === 0;
  const Icon = GROUP_ICON[group] || Settings;

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors">
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <Icon className="w-4 h-4 text-foreground" />
          <span className="font-medium text-sm text-foreground">{group}</span>
          <span className="text-xs text-muted-foreground">{filtered.length} items</span>
        </div>
        <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
          {!allOn && !allOff && <span className="text-xs text-muted-foreground">{enabledCount}/{filtered.length} on</span>}
          {allOn && <span className="text-xs text-muted-foreground">All ON</span>}
          {allOff && <span className="text-xs text-destructive">All OFF</span>}
          <Switch checked={allOn} onCheckedChange={v => onBulkToggle(filtered.map(f => f.key), v)} />
        </div>
      </button>
      {expanded && (
        <div className="divide-y divide-border border-t border-border">
          {filtered.map(f => {
            const enabled = toggleMap.get(f.key) !== false;
            return (
              <div key={f.key} className="flex items-center justify-between px-4 py-2.5 pl-12">
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${enabled ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{f.label}</span>
                    <span className="text-xs text-muted-foreground hidden sm:inline">{f.route}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{f.description}</p>
                </div>
                <Switch checked={enabled} onCheckedChange={v => onToggle(f.key, v)} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PermissionGroupsTab() {
  const groups = ['Owner', 'Admin', 'Manager', 'Supervisor', 'Staff'];
  const [selected, setSelected] = useState('Staff');
  const groupKeys = GROUP_DEFAULTS[selected] || [];
  const isWildcard = groupKeys.includes('*');

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Default feature access by role group. Per-employee overrides are managed in the Admin Panel employee drawer.</p>
      <div className="flex flex-wrap gap-2">
        {groups.map(g => (
          <Button key={g} variant={selected === g ? 'default' : 'outline'} size="sm" onClick={() => setSelected(g)}>{g}</Button>
        ))}
      </div>
      <div className="border border-border rounded-lg bg-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-medium text-sm text-foreground">{selected} Group</h3>
          <p className="text-xs text-muted-foreground">{isWildcard ? 'Full access to all features' : `${groupKeys.length} features enabled`}</p>
        </div>
        <div className="max-h-96 overflow-y-auto divide-y divide-border">
          {NAV_GROUPS.map(ng => {
            const features = getFeaturesByGroup(ng);
            return features.map(f => {
              const allowed = isWildcard || groupKeys.includes(f.key);
              return (
                <div key={f.key} className="flex items-center justify-between px-4 py-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${allowed ? 'text-foreground' : 'text-muted-foreground'}`}>{f.label}</span>
                    <Badge variant="outline" className="text-[10px]">{f.navGroup}</Badge>
                  </div>
                  <Badge variant={allowed ? 'default' : 'secondary'} className="text-[10px]">{allowed ? 'Allowed' : 'Blocked'}</Badge>
                </div>
              );
            });
          })}
        </div>
      </div>
    </div>
  );
}

function ChangeLogTab({ changeLog }: { changeLog: GetFeatureTogglesOutputType['changeLog'] }) {
  if (changeLog.length === 0) return <p className="text-sm text-muted-foreground py-8 text-center">No changes recorded yet.</p>;
  return (
    <div className="space-y-2">
      {changeLog.map(l => (
        <div key={l.id} className="border border-border rounded-lg px-4 py-3 bg-card">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">{l.action}</span>
            <span className="text-xs text-muted-foreground">{l.timestamp ? formatDistanceToNow(parseISO(l.timestamp), { addSuffix: true }) : ''}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{l.actorEmail} → {l.target}</p>
          {l.details && <p className="text-xs text-muted-foreground mt-0.5">{l.details}</p>}
        </div>
      ))}
    </div>
  );
}

export default function AppSettings() {
  const { user } = useAuth();
  const { allToggles, changeLog, reload, isAdmin: ctxAdmin } = useFeatureToggles();
  const [search, setSearch] = useState('');
  const [previewRole, setPreviewRole] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Local toggle map for instant UI updates
  const [localToggles, setLocalToggles] = useState<Map<string, boolean>>(new Map());

  useEffect(() => {
    const m = new Map<string, boolean>();
    allToggles.forEach(t => m.set(t.key, t.enabled));
    // Fill in missing features as enabled
    FEATURES.forEach(f => { if (!m.has(f.key)) m.set(f.key, true); });
    setLocalToggles(m);
  }, [allToggles]);

  if (!isOwnerOrAdmin(user?.role)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-2">
          <Shield className="w-12 h-12 mx-auto text-muted-foreground" />
          <p className="text-muted-foreground">Owner/Admin access required</p>
        </div>
      </div>
    );
  }

  const handleToggle = async (key: string, enabled: boolean) => {
    setLocalToggles(prev => new Map(prev).set(key, enabled));
    setSaving(true);
    try {
      await saveFeatureToggle({ action: 'toggle', featureKey: key, enabled });
      toast.success(`${key} ${enabled ? 'enabled' : 'disabled'}`);
      reload();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleBulkToggle = async (keys: string[], enabled: boolean) => {
    keys.forEach(k => setLocalToggles(prev => new Map(prev).set(k, enabled)));
    setSaving(true);
    try {
      await saveFeatureToggle({ action: 'bulkToggle', featureKeys: keys, enabled });
      toast.success(`${keys.length} features ${enabled ? 'enabled' : 'disabled'}`);
      reload();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const r = await saveFeatureToggle({ action: 'seed' });
      toast.success(r.message);
      reload();
    } catch { toast.error('Failed to seed'); }
    finally { setSeeding(false); }
  };

  const handleExportCsv = () => {
    const headers = ['Current Menu', 'Current Submenu', 'Feature', 'Key', 'Route', 'Feature Description', 'Fully Working / Partial / Placeholder', 'Data Source or Integration', 'Dependencies', 'Acceptance Criteria', 'Evidence / Proof', 'Minimum Role', 'Launch Enabled?', 'Proposed Section', 'Proposed Name', 'Notes / Action Required'];
    const rows = FEATURES.map(f => [
      f.navGroup, f.submenu, f.label, f.key, f.route, f.description, f.status, f.tables, f.dependencies, f.acceptanceCriteria, f.evidence, f.minRole,
      localToggles.get(f.key) !== false ? 'ON' : 'OFF', '', '', '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `staff-hub-feature-audit-${new Date().toISOString().split('T')[0]}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const enabledCount = [...localToggles.values()].filter(Boolean).length;
  const totalCount = FEATURES.length;

  // Preview mode — show which features a role would see
  const previewKeys = previewRole ? (() => {
    const group = previewRole;
    const gk = GROUP_DEFAULTS[group] || GROUP_DEFAULTS.Staff;
    const isWild = gk.includes('*');
    return FEATURES.filter(f => {
      const globallyOn = localToggles.get(f.key) !== false;
      if (!globallyOn) return false;
      return isWild || gk.includes(f.key);
    }).map(f => f.key);
  })() : null;

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">App Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Control which features are visible and who can access them</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Select value={previewRole} onValueChange={setPreviewRole}>
            <SelectTrigger className="w-36 h-9">
              <Eye className="w-3.5 h-3.5 mr-1.5" />
              <SelectValue placeholder="Preview as…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Owner">Owner</SelectItem>
              <SelectItem value="Manager">Manager</SelectItem>
              <SelectItem value="Supervisor">Supervisor</SelectItem>
              <SelectItem value="Staff">Staff</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleExportCsv} className="h-9">
            <Download className="w-3.5 h-3.5 mr-1.5" />CSV
          </Button>
        </div>
      </div>

      {previewRole && previewKeys && (
        <div className="bg-muted/50 border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Preview: {previewRole} view</p>
              <p className="text-xs text-muted-foreground">{previewKeys.length} of {totalCount} features visible</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setPreviewRole('')}>Clear preview</Button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {previewKeys.map(k => {
              const f = FEATURES.find(ff => ff.key === k);
              return <Badge key={k} variant="secondary" className="text-[10px]">{f?.label || k}</Badge>;
            })}
          </div>
        </div>
      )}

      <Tabs defaultValue="toggles">
        <TabsList>
          <TabsTrigger value="toggles">Feature Toggles</TabsTrigger>
          <TabsTrigger value="permissions">Permission Groups</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="changelog">Change Log ({changeLog.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="toggles" className="space-y-4 mt-4">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search features…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            </div>
            <span className="text-xs text-muted-foreground">{totalCount} features · {enabledCount} enabled · {totalCount - enabledCount} disabled</span>
            <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding} className="ml-auto">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${seeding ? 'animate-spin' : ''}`} />
              Sync DB
            </Button>
          </div>

          {NAV_GROUPS.map(g => (
            <ToggleGroup key={g} group={g} toggleMap={localToggles} onToggle={handleToggle} onBulkToggle={handleBulkToggle} search={search} />
          ))}
        </TabsContent>

        <TabsContent value="permissions" className="mt-4">
          <PermissionGroupsTab />
        </TabsContent>

        <TabsContent value="integrations" className="mt-4">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">External integration feeds and sync status.</p>
            <StaffReadinessIntegration />
          </div>
        </TabsContent>

        <TabsContent value="changelog" className="mt-4">
          <ChangeLogTab changeLog={changeLog} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
